import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Loader2, FileText, Save, Award, Calendar } from 'lucide-react';
import { 
  getUserScore,
  UserScore,
  saveQuestionerAnswers,
  QuestionerAnswer
} from '@/lib/firebase/queries/questionersanswers';
import { toScaleQuestions } from '@/lib/firebase/queries/namatScale';
import { getAssignedQuestionnaire } from '@/lib/firebase/queries/questionnaireRules';
import type { Questionnaire } from '@/types/questionnaire';
import { ScaleQuestion } from '@/hooks/use-namat-scale';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { pillarScores } from '@/lib/questionnaires/pillarScores';
import { PatientDemographicsCard } from './PatientDemographicsCard';

interface UnifiedQuestionnaireDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
  periodId?: string; // Optional: for viewing historical data
  readOnly?: boolean; // Optional: for read-only historical view
  onSuccess?: () => void;
}

// Create dynamic schema based on questions
const createQuestionnaireSchema = (questions: ScaleQuestion[]) => {
  const schemaFields: Record<string, z.ZodString> = {};
  
  questions.forEach(question => {
    schemaFields[question.id] = z.string().min(1, 'Please select an answer');
  });
  
  return z.object(schemaFields);
};

export function UnifiedQuestionnaireDialog({
  open,
  onOpenChange,
  customerId,
  customerName,
  periodId,
  readOnly = false,
  onSuccess
}: UnifiedQuestionnaireDialogProps) {
  const [questions, setQuestions] = useState<ScaleQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userScore, setUserScore] = useState<UserScore | null>(null);
  const [currentQuestionnaire, setCurrentQuestionnaire] = useState<Questionnaire | null>(null);
  // Always in edit mode - directly editable
  const { t, dir } = useLanguage();

  const form = useForm<Record<string, string>>({
    resolver: zodResolver(createQuestionnaireSchema(questions)),
    defaultValues: {}
  });

  useEffect(() => {
    if (open && customerId) {
      loadQuestionnaireData();
    }
  }, [open, customerId]);

  const loadQuestionnaireData = async () => {
    try {
      setLoading(true);
      
      // The questionnaire assigned to this beneficiary by the admin's rules (#92)
      const questionnaire = await getAssignedQuestionnaire(customerId);
      const questionsData = toScaleQuestions(questionnaire);
      
      setCurrentQuestionnaire(questionnaire);
      setQuestions(questionsData);
      
      // Load existing answers if any
      const existingData = await getUserScore(customerId, periodId);
      setUserScore(existingData);
      
      // Always in edit mode - no mode switching needed
      
      // Pre-fill form with existing answers
      if (existingData) {
        const formData: Record<string, string> = {};
        existingData.answers.forEach(answer => {
          formData[answer.questionId] = answer.optionId;
        });
        form.reset(formData);
      } else {
        // Reset form with empty values
        const emptyData: Record<string, string> = {};
        questionsData.forEach(question => {
          emptyData[question.id] = '';
        });
        form.reset(emptyData);
      }
      
    } catch (error) {
      console.error('Error loading questionnaire data:', error);
      toast.error(t('dashboard.examiner.customers.questionnaire.loadError') || 'Failed to load questionnaire');
    } finally {
      setLoading(false);
    }
  };



  const calculateScore = (answers: Record<string, string>) => {
    let totalScore = 0;
    let maxPossibleScore = 0;
    const answerDetails: { questionId: string; optionId: string; score: number }[] = [];

    questions.forEach(question => {
      // Find the maximum possible score for this question
      const maxScore = Math.max(...question.options.map(opt => opt.score));
      maxPossibleScore += maxScore;

      const selectedOptionId = answers[question.id];
      if (selectedOptionId) {
        const selectedOption = question.options.find(opt => opt.id === selectedOptionId);
        if (selectedOption) {
          totalScore += selectedOption.score;
          answerDetails.push({
            questionId: question.id,
            optionId: selectedOption.id,
            score: selectedOption.score
          });
        }
      }
    });

    return { totalScore, maxPossibleScore, answerDetails };
  };

  const onSubmit = async (data: Record<string, string>) => {
    try {
      setSaving(true);
      
      if (!currentQuestionnaire) {
        throw new Error('No questionnaire loaded');
      }
      
      // Calculate score
      const { totalScore, maxPossibleScore, answerDetails } = calculateScore(data);
      
      // Convert answers to new format with additional metadata
      const questionerAnswers: QuestionerAnswer[] = answerDetails.map(answer => {
        const question = questions.find(q => q.id === answer.questionId);
        const option = question?.options.find(o => o.id === answer.optionId);
        
        return {
          questionId: answer.questionId,
          optionId: answer.optionId,
          score: answer.score,
          questionText: question?.question,
          optionText: option?.text,
          category: question?.category
        };
      });
      
      // Save to new questionnaire answers collection
      await saveQuestionerAnswers(
        customerId,
        currentQuestionnaire.id,
        currentQuestionnaire.name,
        totalScore,
        questionerAnswers,
        maxPossibleScore,
        periodId,
        'examiner' // createdBy field to track who administered the questionnaire
      );
      
      // Reload data to get updated score
      const updatedData = await getUserScore(customerId);
      setUserScore(updatedData);
      
      toast.success(t('dashboard.examiner.customers.questionnaire.saveSuccess') || 'Questionnaire saved successfully');
      onSuccess?.();
      
    } catch (error) {
      console.error('Error saving questionnaire:', error);
      toast.error(t('dashboard.examiner.customers.questionnaire.saveError') || 'Failed to save questionnaire');
    } finally {
      setSaving(false);
    }
  };

  const allQuestionsAnswered = questions.every(q => form.watch(q.id));
  // Per-pillar performance from the answers on screen (saved answers pre-fill
  // the form), shown on the tabs like the clinical exam's sections.
  const live = pillarScores(questions, form.watch());
  const scorePercentage = userScore ? Math.round((userScore.score / userScore.maxScore) * 100) : 0;

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" dir={dir}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.examiner.customers.questionnaire.title') || 'Questionnaire'} - {customerName}
            </DialogTitle>
            <DialogDescription className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.examiner.customers.questionnaire.description') || 'Loading questionnaire data...'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center p-8 flex-1">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (questions.length === 0) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" dir={dir}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.examiner.customers.questionnaire.title') || 'Questionnaire'} - {customerName}
            </DialogTitle>
            <DialogDescription className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.examiner.customers.questionnaire.noQuestions') || 'No questions available'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-8 text-center flex-1">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {t('dashboard.examiner.customers.questionnaire.noQuestions') || 'No Questions Available'}
            </h3>
            <p className="text-muted-foreground">
              {t('dashboard.examiner.customers.questionnaire.noQuestionsDesc') || 'There are no questionnaire questions configured.'}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" dir={dir}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            {userScore 
              ? (t('dashboard.examiner.customers.questionnaire.editTitle') || 'Edit Questionnaire')
              : (t('dashboard.examiner.customers.questionnaire.fillTitle') || 'Fill Questionnaire')
            } - {customerName}
          </DialogTitle>
          <DialogDescription className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            {userScore
              ? (t('dashboard.examiner.customers.questionnaire.editDesc') || 'Update the questionnaire responses for this customer')
              : (t('dashboard.examiner.customers.questionnaire.fillDesc') || 'Fill out the questionnaire on behalf of this customer')
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className={`flex-1 overflow-y-auto space-y-6 ${dir === 'rtl' ? 'pl-2' : 'pr-2'}`}>
          <PatientDemographicsCard userId={customerId} readOnly={readOnly} />
          {/* Summary Card - Show in view mode or when editing existing */}
          {userScore && (
            <Card>
                             <CardHeader>
                                   <CardTitle className={`flex items-center justify-between ${dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'}`}>
                    <Badge variant={scorePercentage >= 70 ? 'default' : scorePercentage >= 50 ? 'secondary' : 'destructive'}>
                      {scorePercentage}%
                    </Badge>
                     <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'}`}>
                       <Award className="h-5 w-5" />
                       {t('dashboard.examiner.customers.questionnaire.summary') || 'Summary'}
                     </div>
                  </CardTitle>
               </CardHeader>
              <CardContent>
                <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                  <div className={`space-y-1 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard.examiner.customers.questionnaire.totalScore') || 'Total Score'}
                    </p>
                    <p className="text-2xl font-bold">
                      {userScore.score} / {userScore.maxScore}
                    </p>
                  </div>
                  <div className={`space-y-1 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard.examiner.customers.questionnaire.completedAt') || 'Completed At'}
                    </p>
                    <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse justify-end' : 'flex-row justify-start'}`}>
                      <Calendar className="h-4 w-4" />
                      <p className="font-medium">
                        {(() => {
                          try {
                            if (!userScore.completedAt) return 'N/A';
                            const date = userScore.completedAt instanceof Date 
                              ? userScore.completedAt 
                              : new Date(userScore.completedAt);
                            return isNaN(date.getTime()) ? 'N/A' : format(date, 'PPP');
                          } catch {
                            return 'N/A';
                          }
                        })()}
                      </p>
                    </div>
                  </div>
                  <div className={`space-y-1 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard.examiner.customers.questionnaire.totalQuestions') || 'Total Questions'}
                    </p>
                    <p className="text-2xl font-bold">
                      {questions.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Questions Section */}
          <div className="space-y-4">
            <h3 className={`text-lg font-semibold ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
              {t('dashboard.examiner.customers.questionnaire.questionsAndAnswers') || 'Questions and Answers'}
            </h3>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <Tabs defaultValue="all" className="w-full" dir={dir}>
                  <TabsList className="flex w-full h-auto p-1 flex-wrap gap-1">
                    <TabsTrigger value="all" className="text-xs px-2 py-1.5 border border-gray-300 data-[state=active]:border-transparent">
                      <div className="flex items-center gap-1">
                        <span>{t('dashboard.examiner.customers.questionnaire.overall') || 'Overall'}</span>
                        <span className="text-[#7dda55] font-semibold text-xs">{live.overall.percentage}%</span>
                      </div>
                    </TabsTrigger>
                    {live.pillars.map((pillar) => (
                      <TabsTrigger key={pillar.category} value={pillar.category} className="text-xs px-2 py-1.5 border border-gray-300 data-[state=active]:border-transparent">
                        <div className="flex items-center gap-1">
                          <span>{t(`habits.categories.${pillar.category}`) || pillar.category}</span>
                          <span className="text-[#7dda55] font-semibold text-xs">{pillar.percentage}%</span>
                        </div>
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {[{ category: 'all' }, ...live.pillars].map(({ category }) => (
                    <TabsContent key={category} value={category} className="space-y-4">
                      {category !== 'all' && (() => {
                        const pillar = live.pillars.find((p) => p.category === category)!;
                        return (
                          <p className={`text-sm text-muted-foreground ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                            {t('dashboard.examiner.customers.questionnaire.pillarScore') || 'Score'}:{' '}
                            {/* Numbers stay left-to-right so "5/6" never reads as 6/5 in Arabic. */}
                            <span dir="ltr">{pillar.score}/{pillar.max}</span>
                            {' · '}
                            {t('dashboard.examiner.customers.questionnaire.pillarAnswered') || 'Answered'}:{' '}
                            <span dir="ltr">{pillar.answered}/{pillar.total}</span>
                          </p>
                        );
                      })()}
                      {questions.map((question, index) => {
                        if (category !== 'all' && (question.category || 'Other') !== category) return null;
                        
                        return (
                          <Card key={question.id}>
                            <CardContent className="pt-6">
                              <div className="space-y-4">
                                <div className={`flex items-start gap-3 ${dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'}`}>
                                  <Badge variant="outline" className="flex-shrink-0">
                                    {index + 1}
                                  </Badge>
                                  <div className="flex-1">
                                    <p className={`font-medium ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                                      {typeof question.question === 'string' 
                                        ? question.question 
                                        : (dir === 'rtl' ? question.question.ar : question.question.en)
                                      }
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Always show form controls - directly editable */}
                                <div className={`${dir === 'rtl' ? 'mr-8' : 'ml-8'}`}>
                                  <FormField
                                    control={form.control}
                                    name={question.id}
                                    render={({ field }) => (
                                      <FormItem className="space-y-3">
                                        <FormControl>
                                          <RadioGroup
                                            onValueChange={readOnly ? undefined : field.onChange}
                                            value={field.value}
                                            className="space-y-2"
                                            disabled={readOnly}
                                          >
                                            {question.options.filter(option => !option.hidden).map((option) => (
                                              <div key={option.id} className={`flex items-center p-2 rounded-lg border hover:bg-muted/50 transition-colors ${dir === 'rtl' ? 'flex-row-reverse space-x-reverse gap-3' : 'space-x-3'}`}>
                                                <RadioGroupItem value={option.id} id={`${question.id}-${option.id}`} />
                                                <Label htmlFor={`${question.id}-${option.id}`} className={`flex-1 cursor-pointer ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                                                  {typeof option.text === 'string' 
                                                    ? option.text 
                                                    : (dir === 'rtl' ? option.text.ar : option.text.en)
                                                  }
                                                </Label>
                                                <Badge variant="secondary" className="text-xs">
                                                  {option.score}
                                                </Badge>
                                              </div>
                                            ))}
                                          </RadioGroup>
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </TabsContent>
                  ))}
                </Tabs>
              </form>
            </Form>
          </div>
        </div>
        
        {/* Footer with action buttons */}
        <div className={`flex items-center ${readOnly ? 'justify-center' : 'justify-between'} pt-4 border-t flex-shrink-0 ${dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'}`}>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close') || 'Close'}
          </Button>
          
          {!readOnly && (
            <Button
              onClick={form.handleSubmit(onSubmit)}
              disabled={!allQuestionsAnswered || saving}
              className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'}`}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving 
                ? (t('dashboard.examiner.customers.questionnaire.saving') || 'Saving...')
                : (t('dashboard.examiner.customers.questionnaire.save') || 'Save')
              }
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
